import { GeometryType } from "@evergis/api";
import { ButtonsGroup, Input, Link, Radio, Tooltip } from "@evergis/ui";
import React, { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RouteName } from "../../../../appConfig/types";
import { LinearProgress, LoadingDescription } from "../../CopyFeature/styled";

import { ContrastDescr, Descr, DialogContainer, Info, InfoLink, InfoWithBackground } from "../../styled";
import { EXPORT_FILE_TYPES } from "../constants";
import { ExportRadioGroup } from "../styled";
import { ExportDialogContentProps, ExportType } from "../types";

const toCatalog = {
  type: RouteName.Catalog,
  payload: {
    tab: "tables",
  },
};

// eslint-disable-next-line max-lines-per-function
export const ExportDialogContent: FC<ExportDialogContentProps> = memo(
  ({
    layer,
    isLoading,
    isPointData,
    description,
    selectedFormat,
    setSelectedFormat,
    isWkt,
    setIsWkt,
    columnDelimiter,
    setColumnDelimiter,
  }) => {
    const { t } = useTranslation();
    const memoOptions = useMemo(
      () =>
        EXPORT_FILE_TYPES.filter(({ name }) =>
          layer.geometryType === GeometryType.Unknown
            ? ![ExportType.Shape, ExportType.GeoJSON].includes(name as ExportType)
            : true,
        ).map(option => ({ label: option.name })),
      [layer],
    );

    return (
      <DialogContainer>
        {isLoading ? (
          <>
            <LoadingDescription>{t("actions.loading", { ns: "common" })}</LoadingDescription>
            <LinearProgress />
          </>
        ) : (
          <>
            <ContrastDescr>{description}</ContrastDescr>
            <Info>
              {t("export.allDataHint", { ns: "dialogs" })}{" "}
              <InfoLink to={toCatalog}>{t("export.inCatalog", { ns: "dialogs" })}</InfoLink>
            </Info>
            <Descr>{t("export.chooseFormat", { ns: "dialogs" })}:</Descr>
            <ButtonsGroup
              className="buttons-group"
              options={memoOptions}
              selected={selectedFormat}
              onClick={setSelectedFormat}
            />
            {isPointData ? (
              <>
                {(selectedFormat.label === "Csv" || selectedFormat.label === "XLSX") && (
                  <ExportRadioGroup
                    name={"wkt status"}
                    value={isWkt ? "WKT" : "XY"}
                    onChange={e => {
                      setIsWkt(e.target.value === "WKT");
                    }}
                    column
                  >
                    <Radio value="WKT">{t("export.wktGeometry", { ns: "dialogs" })} </Radio>
                    <Radio value="XY"> {t("export.XYGeometry", { ns: "dialogs" })} </Radio>
                  </ExportRadioGroup>
                )}
                {selectedFormat.label === "Csv" && (
                  <Info>
                    <Input
                      label={t("export.columnDelimetr", { ns: "dialogs" })}
                      value={columnDelimiter}
                      // help={t("export.columnDelimetrHelp", { ns: "dialogs" })}
                      onChange={e => {
                        setColumnDelimiter(e.target.value);
                      }}
                    />
                    {t("export.commonUse", { ns: "dialogs" })}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                      description
                      onClick={() => {
                        setColumnDelimiter(",");
                      }}
                    >
                      {t("export.comma", { ns: "dialogs" })}
                    </Link>{" "}
                    {t("export.or", { ns: "dialogs" })}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                      description
                      onClick={() => {
                        setColumnDelimiter(";");
                      }}
                    >
                      {t("export.semicolon", { ns: "dialogs" })}
                    </Link>
                  </Info>
                )}
              </>
            ) : (
              (selectedFormat.label === "Csv" || selectedFormat.label === "XLSX") &&
              layer.geometryType !== GeometryType.Unknown && (
                <InfoWithBackground>
                  {t("export.infoWKT", { ns: "dialogs" })}{" "}
                  <Tooltip content={t("export.wktHint", { ns: "dialogs" })}>
                    {ref => (
                      /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                      <Link description ref={ref}>
                        {" "}
                        WKT
                      </Link>
                    )}
                  </Tooltip>
                </InfoWithBackground>
              )
            )}
          </>
        )}
      </DialogContainer>
    );
  },
);
